/**
 * The external participant portal.
 *
 * A different product for a different person: a client who was in a meeting,
 * arriving from an email, who has no account here. It lives outside the app
 * shell for the same reason `/share/:token` does — a sidebar offering Chat,
 * Templates and Spaces would be offering everything except the thing they came
 * for, and most of it is not theirs to see.
 *
 * One route renders all of it: signed out, or the workspace — meetings on the
 * left, the assistant on the right — with an opened meeting taking over the
 * left half. They are steps through one short task rather than places worth
 * their own URLs to a reader who arrives by link. The exception is a meeting id
 * in the address, which the email does link to directly.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  CalendarDays,
  Check,
  Copy,
  Download,
  LogOut,
  Mail,
  MessageSquare,
  Send,
  Square,
  Users,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Markdown } from '@/components/Markdown'
import { Button } from '@/components/ui/button'
import { SegmentedControl, Tab, TabList, TabPanel, Tabs } from '@/components/ui/controls'
import { EmptyState, Panel, Skeleton } from '@/components/ui/display'
import { Field, Input, Textarea } from '@/components/ui/field'
import {
  checkChatContext,
  exchangeLoginToken,
  getPortalMeeting,
  getPortalOverview,
  portalChat,
  portalLogout,
  PortalUnauthorized,
  requestLoginLink,
  type PortalMeeting,
  type PortalMeetingDetail,
  type PortalOverview,
  type PortalSource,
} from '@/data/portal'
import { cn } from '@/lib/utils'

/** Openers for a reader who has just added a meeting and has no question yet. */
const STARTERS = [
  'Summarize the key decisions',
  'What follow-ups were agreed?',
  'Compare the main themes',
]

export function PortalPage({ meetingId }: { meetingId?: string | null }) {
  const queryClient = useQueryClient()
  /**
   * Which meeting to open.
   *
   * Either the route said so (`/portal/meetings/:id`), or the emailed link did:
   * a share email points at `/portal/sign-in?token=…&meeting=…`, and dropping
   * that second parameter lands a client who was sent one meeting on a list
   * containing it — the link stops being a link to anything.
   */
  const [open, setOpen] = useState<string | null>(
    () => meetingId ?? new URLSearchParams(window.location.search).get('meeting')
  )

  /**
   * A token in the address is a fresh link from an email; it is exchanged for a
   * session and then removed, so a reload does not try to spend it twice and a
   * forwarded address is not a working credential.
   */
  const [exchanging, setExchanging] = useState(() =>
    new URLSearchParams(window.location.search).has('token')
  )
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (!token) return
    void exchangeLoginToken(token)
      .catch(() => undefined)
      .finally(() => {
        params.delete('token')
        const query = params.toString()
        window.history.replaceState({}, '', `/portal${query ? `?${query}` : ''}`)
        setExchanging(false)
        void queryClient.invalidateQueries({ queryKey: ['portal'] })
      })
  }, [queryClient])

  const overview = useQuery({
    queryKey: ['portal', 'overview'],
    queryFn: getPortalOverview,
    enabled: !exchanging,
    retry: false,
  })

  if (exchanging || overview.isLoading) return <PortalFrame><Loading /></PortalFrame>
  if (overview.error instanceof PortalUnauthorized || (overview.error && !overview.data)) {
    return <PortalFrame><SignIn /></PortalFrame>
  }
  if (!overview.data) return <PortalFrame><SignIn /></PortalFrame>

  return (
    <PortalFrame email={overview.data.email} layout="workspace">
      <Workspace overview={overview.data} open={open} onOpen={setOpen} />
    </PortalFrame>
  )
}

/**
 * Meetings on the left, the assistant on the right.
 *
 * The two halves are one task, not two pages: a reader picks meetings to ask
 * about and then asks, and a chat that replaced the list would hide the thing
 * being discussed at the moment of discussing it. Opening a meeting therefore
 * takes over the left half only — the conversation and its selection survive it.
 *
 * The selection is what the question is answered from, so it is deliberately
 * explicit. Every meeting the reader may see is one they were in, and the
 * backend re-resolves each id against that access on every request; the button
 * chooses what to read, not what is permitted.
 */
function Workspace({
  overview,
  open,
  onOpen,
}: {
  overview: PortalOverview
  open: string | null
  onOpen: (id: string | null) => void
}) {
  const [selected, setSelected] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pane, setPane] = useState<'meetings' | 'chat'>('meetings')

  const byId = new Map<string, PortalMeeting>()
  for (const group of overview.groups) for (const meeting of group.meetings) byId.set(meeting.id, meeting)
  for (const meeting of overview.direct) byId.set(meeting.id, meeting)
  const selectedMeetings = selected
    .map((id) => byId.get(id))
    .filter((meeting): meeting is PortalMeeting => Boolean(meeting))

  const toggle = async (id: string) => {
    if (selected.includes(id)) {
      setSelected((current) => current.filter((entry) => entry !== id))
      setError(null)
      return
    }
    if (!byId.get(id)?.hasTranscript) {
      setError('That meeting has no transcript to ask about yet.')
      return
    }

    const candidate = [...selected, id]
    setBusy(true)
    setError(null)
    try {
      const { fits } = await checkChatContext(candidate)
      if (!fits) {
        setError('That selection is too large for one chat. Remove a meeting before adding this one.')
        return
      }
      setSelected(candidate)
      setPane('chat')
    } catch (chatError) {
      setError((chatError as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const groups = overview.groups.filter((group) => group.meetings.length > 0)

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <div className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 lg:hidden">
        <SegmentedControl
          className="w-full"
          value={pane}
          onValueChange={setPane}
          options={[
            { value: 'meetings', label: 'Meetings' },
            { value: 'chat', label: selected.length ? `Chat · ${selected.length}` : 'Chat' },
          ]}
        />
      </div>

      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto px-4 py-6',
          pane === 'meetings' ? 'block' : 'hidden lg:block'
        )}
      >
        <div className="mx-auto max-w-3xl">
          {open ? (
            <MeetingView
              id={open}
              inChat={selected.includes(open)}
              busy={busy}
              onToggleChat={() => void toggle(open)}
              onBack={() => onOpen(null)}
            />
          ) : overview.total === 0 ? (
            <EmptyState
              title="Nothing shared with you yet."
              description="When someone shares a meeting you were in, it appears here."
            />
          ) : (
            <div className="space-y-6">
              {groups.map((group) => (
                <section key={group.id} className="space-y-2">
                  <h2 className="text-xs font-medium text-[var(--color-fg-muted)]">{group.name}</h2>
                  <MeetingList
                    meetings={group.meetings}
                    selected={selected}
                    busy={busy}
                    onOpen={onOpen}
                    onToggleChat={(id) => void toggle(id)}
                  />
                </section>
              ))}
              {overview.direct.length > 0 ? (
                <section className="space-y-2">
                  {groups.length > 0 ? (
                    <h2 className="text-xs font-medium text-[var(--color-fg-muted)]">Other meetings</h2>
                  ) : null}
                  <MeetingList
                    meetings={overview.direct}
                    selected={selected}
                    busy={busy}
                    onOpen={onOpen}
                    onToggleChat={(id) => void toggle(id)}
                  />
                </section>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <div
        className={cn(
          'min-h-0 border-[var(--color-border)] bg-[var(--color-surface)]',
          'flex-1 lg:w-[26rem] lg:flex-none lg:border-l xl:w-[clamp(28rem,30vw,40rem)]',
          pane === 'chat' ? 'flex' : 'hidden lg:flex'
        )}
      >
        <PortalChat
          meetings={selectedMeetings}
          busy={busy}
          error={error}
          onRemove={(id) => void toggle(id)}
          onOpenMeeting={onOpen}
        />
      </div>
    </div>
  )
}

/**
 * The bar, and what sits under it.
 *
 * Signing in is a single centred column; the workspace is the full window, so
 * the two panes can each scroll on their own rather than the page scrolling
 * both.
 */
function PortalFrame({
  email,
  layout = 'centered',
  children,
}: {
  email?: string
  layout?: 'centered' | 'workspace'
  children: React.ReactNode
}) {
  const logout = useMutation({
    mutationFn: portalLogout,
    onSuccess: () => window.location.reload(),
  })

  return (
    <div
      className={cn(
        'flex flex-col bg-[var(--color-canvas)]',
        layout === 'workspace' ? 'h-dvh overflow-hidden' : 'min-h-dvh'
      )}
    >
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4">
        <span className="text-xs font-semibold text-[var(--color-fg)]">Meeting notes</span>
        {email ? (
          <div className="flex items-center gap-2">
            <span className="text-2xs text-[var(--color-fg-subtle)]">{email}</span>
            <Button variant="ghost" size="sm" onClick={() => logout.mutate()}>
              <LogOut className="size-3.5" />
              Sign out
            </Button>
          </div>
        ) : null}
      </header>
      {layout === 'workspace' ? (
        children
      ) : (
        <main className="mx-auto w-full max-w-3xl px-4 py-8">{children}</main>
      )}
    </div>
  )
}

function Loading() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-14" />
      <Skeleton className="h-14" />
      <Skeleton className="h-14" />
    </div>
  )
}

/**
 * Asking for a link.
 *
 * The confirmation is the same whatever the address, because a different answer
 * for an unknown one turns this box into a way to ask whether a given person
 * was in any of the firm's meetings.
 */
function SignIn() {
  const [email, setEmail] = useState('')
  const request = useMutation({ mutationFn: () => requestLoginLink(email) })

  if (request.isSuccess) {
    return (
      <EmptyState
        icon={Mail}
        title="Check your email."
        description={`If ${email.trim()} has meetings shared with it, a sign-in link is on its way. The link works once and expires.`}
      />
    )
  }

  return (
    <div className="mx-auto max-w-sm space-y-4 py-8">
      <div>
        <h1 className="text-base font-semibold text-[var(--color-fg)]">
          See the meetings you were in
        </h1>
        <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
          Enter the address the invitation was sent to and we will email you a link. No password.
        </p>
      </div>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (email.trim()) request.mutate()
        }}
      >
        <Field label="Email">
          {(props) => (
            <Input
              {...props}
              autoFocus
              type="email"
              value={email}
              placeholder="you@company.com"
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Button type="submit" className="w-full" disabled={!email.trim() || request.isPending}>
          Email me a link
        </Button>
      </form>
    </div>
  )
}

/**
 * A meeting in the list: open it, or add it to the conversation.
 *
 * Two actions on one row, so the row itself is not a button — the title opens
 * the meeting, and Ask sits beside it, because a reader assembling a question
 * out of three calls should not have to open all three to do it.
 */
function MeetingList({
  meetings,
  selected,
  busy,
  onOpen,
  onToggleChat,
}: {
  meetings: PortalMeeting[]
  selected: string[]
  busy: boolean
  onOpen: (id: string) => void
  onToggleChat: (id: string) => void
}) {
  return (
    <Panel className="divide-y divide-[var(--color-border-subtle)]">
      {meetings.map((meeting) => {
        const inChat = selected.includes(meeting.id)
        return (
          <div
            key={meeting.id}
            className="flex items-start gap-3 px-3 py-2.5 transition-colors hover:bg-[var(--color-surface-hover)]"
          >
            <button
              type="button"
              onClick={() => onOpen(meeting.id)}
              className="min-w-0 flex-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35"
            >
              <span className="block truncate text-xs font-medium text-[var(--color-fg)]">
                {meeting.title}
              </span>
              <span className="mt-0.5 flex items-center gap-2.5 text-2xs text-[var(--color-fg-subtle)]">
                {meeting.startedAt ? (
                  <span className="flex items-center gap-1">
                    <CalendarDays className="size-2.5" />
                    {new Date(meeting.startedAt).toLocaleDateString()}
                  </span>
                ) : null}
                <span className="flex items-center gap-1">
                  <Users className="size-2.5" />
                  {meeting.participantCount}
                </span>
              </span>
              {meeting.summary ? (
                <span className="mt-1 line-clamp-2 block text-2xs leading-5 text-[var(--color-fg-muted)]">
                  {meeting.summary}
                </span>
              ) : null}
            </button>
            <Button
              variant={inChat ? 'secondary' : 'ghost'}
              size="sm"
              disabled={busy || !meeting.hasTranscript}
              title={meeting.hasTranscript ? undefined : 'No transcript to ask about yet'}
              onClick={() => onToggleChat(meeting.id)}
            >
              {inChat ? <Check className="size-3" /> : <MessageSquare className="size-3" />}
              {inChat ? 'Added' : 'Ask'}
            </Button>
          </div>
        )
      })}
    </Panel>
  )
}

/** "26 August 2026 · 1h 25m" — the header's second line, absent when unknown. */
function meetingMeta(startedAt: string | null, durationSeconds: number | null): string | null {
  const parts: string[] = []
  if (startedAt) {
    parts.push(
      new Date(startedAt).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    )
  }
  if (durationSeconds && durationSeconds > 0) {
    const minutes = Math.round(durationSeconds / 60)
    parts.push(minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

function safeFilename(value: string): string {
  return value.trim().replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '') || 'meeting'
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

/**
 * One meeting: an overview, the transcript beside it, then a place to ask.
 *
 * The overview is the whole meeting in the order a reader wants it — what it
 * was about, the recording to check a passage against, the notes written from
 * it, who was there. The transcript is a tab rather than a section because it
 * is reference material: a reader consults it, they do not scroll through it to
 * reach anything else.
 */
function MeetingView({
  id,
  inChat,
  busy,
  onToggleChat,
  onBack,
}: {
  id: string
  inChat: boolean
  busy: boolean
  onToggleChat: () => void
  onBack: () => void
}) {
  const meeting = useQuery({
    queryKey: ['portal', 'meeting', id],
    queryFn: () => getPortalMeeting(id),
    retry: false,
  })

  const data = meeting.data
  const meta = data ? meetingMeta(data.startedAt, data.durationSeconds) : null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="size-3.5" />
          All meetings
        </Button>
        {data ? (
          <Button
            variant={inChat ? 'secondary' : 'ghost'}
            size="sm"
            disabled={busy || !data.transcript}
            title={data.transcript ? undefined : 'No transcript to ask about yet'}
            onClick={onToggleChat}
          >
            {inChat ? <Check className="size-3" /> : <MessageSquare className="size-3" />}
            {inChat ? 'Added to chat' : 'Ask about this'}
          </Button>
        ) : null}
      </div>

      {meeting.isLoading ? (
        <Loading />
      ) : data ? (
        <>
          <div>
            <h1 className="text-base font-semibold text-[var(--color-fg)]">{data.title}</h1>
            {meta ? <p className="mt-1 text-2xs text-[var(--color-fg-subtle)]">{meta}</p> : null}
          </div>

          <Tabs defaultValue="overview">
            <TabList>
              <Tab value="overview" className="text-xs">
                Overview
              </Tab>
              <Tab value="transcript" className="text-xs">
                Transcript
              </Tab>
            </TabList>

            <TabPanel value="overview" className="space-y-4 pt-4">
              {data.summary ? (
                <p className="text-xs leading-6 text-[var(--color-fg-muted)]">{data.summary}</p>
              ) : null}

              {data.mediaUrl ? <Recording meeting={data} /> : null}

              {data.notes.map((note) => (
                <Panel key={note.id}>
                  <div className="border-b border-[var(--color-border-subtle)] px-3 py-2">
                    <h2 className="text-xs font-medium text-[var(--color-fg)]">{note.title}</h2>
                  </div>
                  <div className="px-3 py-2.5">
                    <Markdown>{note.body}</Markdown>
                  </div>
                </Panel>
              ))}

              {data.participants.length > 0 ? (
                <div className="space-y-1">
                  <h2 className="text-xs font-medium text-[var(--color-fg)]">Participants</h2>
                  <p className="text-2xs leading-5 text-[var(--color-fg-subtle)]">
                    {data.participants.join(', ')}
                  </p>
                </div>
              ) : null}

              {!data.summary && !data.mediaUrl && data.notes.length === 0 ? (
                <EmptyState title="No overview is available for this meeting." />
              ) : null}
            </TabPanel>

            <TabPanel value="transcript" className="pt-4">
              <TranscriptPanel meeting={data} />
            </TabPanel>
          </Tabs>
        </>
      ) : (
        <EmptyState title="That meeting is not available." />
      )}
    </div>
  )
}

/**
 * The recording, played in the page.
 *
 * A `<video>` element for a FLAC renders a black rectangle that happens to make
 * sound, so the element follows what was actually stored rather than what the
 * field used to be called.
 */
function Recording({ meeting }: { meeting: PortalMeetingDetail }) {
  const url = meeting.mediaUrl
  if (!url) return null
  const isVideo = meeting.mediaKind !== 'audio'

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xs font-medium text-[var(--color-fg)]">Recording</h2>
        <Button variant="ghost" size="sm" onClick={() => window.open(url, '_blank', 'noopener')}>
          <Download className="size-3" />
          Download
        </Button>
      </div>
      {isVideo ? (
        <video
          src={url}
          controls
          preload="metadata"
          className="aspect-video w-full rounded-[var(--radius-lg)] bg-black"
        />
      ) : (
        <audio src={url} controls preload="metadata" className="w-full" />
      )}
    </div>
  )
}

function TranscriptPanel({ meeting }: { meeting: PortalMeetingDetail }) {
  const [copied, setCopied] = useState(false)

  if (!meeting.transcript) {
    return <EmptyState title="This meeting does not have a transcript." />
  }

  const transcript = meeting.transcript

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(transcript).then(() => {
              setCopied(true)
              window.setTimeout(() => setCopied(false), 1600)
            })
          }}
        >
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            downloadBlob(
              new Blob([transcript], { type: 'text/plain;charset=utf-8' }),
              `${safeFilename(meeting.title)}-transcript.txt`
            )
          }
        >
          <Download className="size-3" />
          Download
        </Button>
      </div>
      <Panel className="max-h-[32rem] overflow-y-auto px-3 py-2.5">
        <pre className="font-sans text-xs leading-6 whitespace-pre-wrap text-[var(--color-fg-muted)]">
          {transcript}
        </pre>
      </Panel>
    </div>
  )
}

/**
 * The assistant, answering from the meetings the reader picked.
 *
 * It reads those transcripts and nothing else. The ids are sent rather than
 * searched for, and the backend re-resolves each one against what this address
 * may see, so a question can never widen into the firm's other calls — the
 * selection chooses among the reader's own meetings, it does not grant any.
 */
function PortalChat({
  meetings,
  busy,
  error,
  onRemove,
  onOpenMeeting,
}: {
  meetings: PortalMeeting[]
  busy: boolean
  error: string | null
  onRemove: (id: string) => void
  onOpenMeeting: (id: string) => void
}) {
  const [question, setQuestion] = useState('')
  const [turns, setTurns] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([])
  const [running, setRunning] = useState(false)
  const [sources, setSources] = useState<PortalSource[]>([])
  const abort = useRef<AbortController | null>(null)
  const thread = useRef<HTMLDivElement | null>(null)

  const meetingIds = meetings.map((meeting) => meeting.id)
  const ready = meetingIds.length > 0

  useEffect(() => {
    thread.current?.scrollTo({ top: thread.current.scrollHeight })
  }, [turns])

  /**
   * A `[[1]]` the model wrote, resolved to the transcript it names.
   *
   * With several meetings in one answer the marker has to say which one it
   * came from, so it opens that meeting rather than merely naming it. An
   * unknown reference renders as written, which is the honest rendering of a
   * citation pointing at nothing.
   */
  const renderCitation = useCallback(
    (ref: string) => {
      const source = sources.find((entry) => entry.ref === ref)
      if (!source) return <>[[{ref}]]</>
      return (
        <button
          type="button"
          title={source.title}
          onClick={() => onOpenMeeting(source.meeting_id)}
          className={cn(
            'mx-0.5 inline-grid h-4 min-w-4 place-items-center rounded-[var(--radius-xs)] px-1',
            'align-[0.1em] text-[10px] font-medium tabular-nums',
            'bg-[var(--color-citation-bg)] text-[var(--color-citation)] hover:underline'
          )}
        >
          {ref}
        </button>
      )
    },
    [sources, onOpenMeeting]
  )

  const ask = async (text: string) => {
    if (!text.trim() || running || !ready) return
    setQuestion('')
    setRunning(true)
    const history = [...turns, { role: 'user' as const, content: text.trim() }]
    setTurns([...history, { role: 'assistant', content: '' }])

    abort.current?.abort()
    abort.current = new AbortController()
    try {
      for await (const event of portalChat({ messages: history, meetingIds }, abort.current.signal)) {
        if (event.type === 'context') {
          setSources(event.sources)
        }
        if (event.type === 'delta') {
          setTurns((current) =>
            current.map((turn, index) =>
              index === current.length - 1 ? { ...turn, content: turn.content + event.text } : turn
            )
          )
        }
        if (event.type === 'error') {
          setTurns((current) =>
            current.map((turn, index) =>
              index === current.length - 1 ? { ...turn, content: event.message } : turn
            )
          )
        }
      }
    } catch (chatError) {
      setTurns((current) =>
        current.map((turn, index) =>
          index === current.length - 1
            ? { ...turn, content: (chatError as Error).message }
            : turn
        )
      )
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="flex min-h-0 w-full flex-col">
      <header className="flex h-11 shrink-0 items-center justify-between border-b border-[var(--color-border)] px-3">
        <h2 className="text-xs font-medium text-[var(--color-fg)]">Meeting assistant</h2>
        {turns.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              abort.current?.abort()
              setTurns([])
              setSources([])
            }}
          >
            New chat
          </Button>
        ) : null}
      </header>

      <div ref={thread} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {turns.length === 0 ? (
          <div className="px-2 py-10 text-center">
            <p className="text-xs font-medium text-[var(--color-fg)]">
              {ready ? 'What would you like to know?' : 'Start with a meeting'}
            </p>
            <p className="mx-auto mt-1 max-w-xs text-2xs leading-5 text-[var(--color-fg-subtle)]">
              {ready
                ? 'Answers come only from the transcripts you selected.'
                : 'Choose Ask beside a meeting to add its transcript.'}
            </p>
            {ready ? (
              <div className="mx-auto mt-4 grid max-w-xs gap-1.5">
                {STARTERS.map((prompt) => (
                  <Button
                    key={prompt}
                    variant="secondary"
                    size="sm"
                    className="w-full justify-start"
                    disabled={running || busy}
                    onClick={() => void ask(prompt)}
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          turns.map((turn, index) => (
            <div
              key={index}
              className={cn(
                'text-xs',
                turn.role === 'user'
                  ? 'font-medium text-[var(--color-fg)]'
                  : 'text-[var(--color-fg-muted)]'
              )}
            >
              {turn.role === 'assistant' ? (
                turn.content ? (
                  <Markdown renderCitation={renderCitation}>{turn.content}</Markdown>
                ) : (
                  <span className="text-[var(--color-fg-subtle)]">Reading the transcripts…</span>
                )
              ) : (
                turn.content
              )}
            </div>
          ))
        )}
      </div>

      <footer className="shrink-0 border-t border-[var(--color-border)] p-3">
        {meetings.length > 0 ? (
          <div className="mb-2 flex max-h-20 flex-wrap gap-1.5 overflow-y-auto">
            {meetings.map((meeting) => (
              <span
                key={meeting.id}
                className="inline-flex max-w-full items-center gap-1 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface-sunken)] py-0.5 pr-1 pl-2 text-2xs text-[var(--color-fg-muted)]"
              >
                <button
                  type="button"
                  className="max-w-48 truncate hover:underline"
                  onClick={() => onOpenMeeting(meeting.id)}
                >
                  {meeting.title}
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${meeting.title}`}
                  disabled={busy || running}
                  className="rounded-[var(--radius-xs)] p-0.5 hover:text-[var(--color-fg)] disabled:opacity-40"
                  onClick={() => onRemove(meeting.id)}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="mb-2 text-2xs text-[var(--color-danger)]">
            {error}
          </p>
        ) : null}

        <div className="flex items-end gap-2">
          <Textarea
            rows={2}
            value={question}
            placeholder={ready ? 'Ask about the selected meetings…' : 'Select a meeting to start'}
            disabled={running || !ready}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                void ask(question)
              }
            }}
          />
          {running ? (
            <Button size="sm" variant="secondary" onClick={() => abort.current?.abort()}>
              <Square className="size-3 fill-current" />
            </Button>
          ) : (
            <Button size="sm" disabled={!question.trim() || !ready} onClick={() => void ask(question)}>
              <Send className="size-3.5" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  )
}
